import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { Form, message } from 'antd';
import { Routes } from 'config/routes';
import {
  GetUserQuery,
  PermissionsEnum,
  useGetUserQuery,
  UserOutput,
  UserRole,
  useUpdateUserInfoMutation,
} from 'gql/types';
import EditUserEmailForm from 'modules/admin/components/EditUserForm/EmailSection';
import { UserType } from 'modules/admin/components/EditUserForm/EmailSection/enum';
import EditUserRoleForm from 'modules/admin/components/EditUserForm/RoleSection';
import { GET_USER_QUERY } from 'modules/admin/graphql/queries';
import { Button, FormItem, Spin } from 'modules/common/components';
import AssignedLocationsSelect from 'modules/common/components/AssignedLocationsSelect';
import AssignedRegionsSelect from 'modules/common/components/AssignedRegionsSelect';
import { UnsavedChangesModal } from 'modules/common/components/UnsavedChangesModal';
import { useUnsavedChangesWarning } from 'modules/common/hooks/useUnsavedChangesWarning';
import { ProductRegionSelect } from 'modules/donor/components';
import AssignedOfficeSelect from 'modules/user/components/AssignedOfficeSelect';
import {
  GET_CURRENT_USER_QUERY,
  GET_USER_LIST,
} from 'modules/user/graphql/queries/user';
import { useCurrentUser } from 'modules/user/hooks/useCurrentUser';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import fields from './fields';
import { editUserInfoSchema, EditUserInfoSchema } from './schema';
import styles from './styles.module.scss';

const {
  UpdateUserEmail,
  ChangeUserRole,
  UpdateOwnProfile,
  UpdateProductLocationPreference,
} = PermissionsEnum;

type Props = {
  ownProfile?: boolean;
  userId?: string;
};

const EditUserForm: FC<Props> = ({ ownProfile, userId }) => {
  const { t } = useTranslation('admin.EditUserForm');

  const [formEnabled, setFormEnabled] = useState<boolean>(false);

  const navigate = useNavigate();

  const currentUser = useCurrentUser();
  const currentUserRole = currentUser.data?.role?.value;

  // Only Admins and Medical directors
  // TODO: Add permission to the matrix
  const canEditLocationSettings =
    currentUserRole === UserRole.Admin ||
    currentUserRole === UserRole.MedicalDirector;

  const allowToChangeEmail = currentUser.isCan(UpdateUserEmail);
  const allowToChangeUserRole = currentUser.isCan(ChangeUserRole);
  const allowToEditOwnProfile = currentUser.isCan(UpdateOwnProfile);
  const allowToChangeProductRegionPreference = currentUser.isCan(
    UpdateProductLocationPreference
  );

  const {
    control,
    formState: { errors, isValid, isDirty },
    reset,
    handleSubmit,
    setValue,
    getValues,
  } = useForm<EditUserInfoSchema>({
    resolver: yupResolver(editUserInfoSchema),
    mode: 'onChange',
  });

  const resetForm = (data: GetUserQuery | undefined) => {
    const assignedLocationIds =
      data?.getUser.assignedLocations?.map((loc) => loc.id) ?? [];

    const assignedRegionIds =
      data?.getUser.assignedRegions?.map((reg) => reg.id) ?? [];

    reset({
      firstName: data?.getUser.firstName,
      lastName: data?.getUser.lastName,
      phoneNumber: data?.getUser.phoneNumber || '',
      productRegionPreference: data?.getUser.productRegionPreference?.id,
      assignedOffice: data?.getUser.assignedOffice?.id,
      assignedRegionIds,
      assignedLocationIds,
    });
  };

  const userQuery = useGetUserQuery({
    variables: {
      input: {
        id: (ownProfile ? String(currentUser.data?.shortId) : userId) || '',
      },
    },
    onError: (error) => {
      message.error(error.message);

      if (error.message === 'Not Found') {
        navigate(Routes.NotFound);
        return;
      }

      navigate(Routes.Users);
    },
    onCompleted: (data) => resetForm(data),
  });

  const [updateUserInfo, updateUserInfoMutation] = useUpdateUserInfoMutation({
    refetchQueries: [GET_USER_QUERY, GET_USER_LIST, GET_CURRENT_USER_QUERY],
    onCompleted: async () => {
      message.success(t('successfullySaved'));
      if (ownProfile && currentUser.refetchData) {
        await currentUser.refetchData();
      }
    },
    onError: (error) => message.error(error.message),
  });

  const submitFormData = async (formData: EditUserInfoSchema) => {
    const {
      productRegionPreference,
      assignedOffice,
      assignedRegionIds,
      assignedLocationIds,
      ...otherData
    } = formData;

    const input: any = {
      id: userQuery?.data?.getUser.id || '',
      productRegionPreferenceId: productRegionPreference,
      assignedOfficeId: assignedOffice,
      assignedRegionIds: assignedRegionIds ?? [],
      assignedLocationIds: assignedLocationIds ?? [],
      ...otherData,
    };

    if (canEditLocationSettings) {
      input.assignedRegionIds = assignedRegionIds ?? [];
      input.assignedLocationIds = assignedLocationIds ?? [];
    }

    updateUserInfo({
      variables: {
        input,
      },
    });

    reset(formData);
    setFormEnabled(!formEnabled);
  };

  const editUserHandler = handleSubmit(submitFormData);

  const handleSave = async () => {
    const formData = getValues();
    await submitFormData(formData);
  };

  const closeHandler = () => {
    resetForm(userQuery.data);
    setFormEnabled(false);
  };

  const {
    isModalOpen,
    handleStay,
    handleDiscard,
    handleSaveAndLeave,
    canSaveAndLeave,
  } = useUnsavedChangesWarning({
    isDirty,
    onSave: handleSave,
    isFormValid: isValid,
  });

  const isLoading = userQuery.loading || updateUserInfoMutation.loading;

  return (
    <>
      <Spin spinning={isLoading}>
        <Form className={styles.form} layout="vertical">
          {fields.map(({ label, name, Component }) => {
            const error = errors[name]?.message;

            return (
              <FormItem
                key={name}
                label={label}
                validateStatus={error && 'error'}
                extra={error}
              >
                <Component
                  key={name}
                  name={name}
                  control={control}
                  disabled={!formEnabled}
                />
              </FormItem>
            );
          })}
          {userQuery?.data?.getUser && (
            <EditUserEmailForm
              user={userQuery?.data?.getUser as UserOutput}
              canEdit={
                ownProfile
                  ? allowToEditOwnProfile && allowToChangeEmail
                  : allowToChangeEmail
              }
              userType={UserType.InternalTeam}
            />
          )}

          {userQuery?.data?.getUser && allowToChangeUserRole && !ownProfile && (
            <EditUserRoleForm user={userQuery?.data?.getUser as UserOutput} />
          )}

          {userQuery?.data?.getUser && (
            <ProductRegionSelect
              control={{ control }}
              disabled={!formEnabled || !canEditLocationSettings}
              userRole={userQuery?.data?.getUser.role}
            />
          )}

          {userQuery?.data?.getUser && (
            <AssignedRegionsSelect
              control={{ control }}
              disabled={!formEnabled || !canEditLocationSettings}
            />
          )}

          {userQuery?.data?.getUser && (
            <AssignedLocationsSelect
              control={control}
              setValue={setValue}
              disabled={!formEnabled || !canEditLocationSettings}
            />
          )}

          {userQuery?.data?.getUser && allowToChangeProductRegionPreference && (
            <AssignedOfficeSelect
              control={{ control }}
              disabled={!formEnabled}
              userRole={userQuery?.data?.getUser.role}
            />
          )}

          {!(ownProfile && !allowToEditOwnProfile) && (
            <div className={styles.buttonsContainer}>
              <Button onClick={closeHandler} disabled={!formEnabled}>
                {t('close')}
              </Button>

              {formEnabled && (
                <Button
                  disabled={!isValid || !isDirty}
                  onClick={editUserHandler}
                  type="primary"
                >
                  {t('save')}
                </Button>
              )}

              {!formEnabled && (
                <Button
                  type="primary"
                  onClick={() => {
                    setFormEnabled(!formEnabled);
                    resetForm(userQuery.data);
                  }}
                >
                  {t('edit')}
                </Button>
              )}
            </div>
          )}
        </Form>
      </Spin>

      <UnsavedChangesModal
        isOpen={isModalOpen}
        onStay={handleStay}
        onDiscard={handleDiscard}
        onSaveAndLeave={handleSaveAndLeave}
        canSaveAndLeave={canSaveAndLeave}
        isSaving={updateUserInfoMutation.loading}
      />
    </>
  );
};
export default EditUserForm;
